import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Typography from "@mui/material/Typography";
import { sxRoot, sxText, sxTypo, sxBox } from "../../Helpers/helpers";
import TextField from "@mui/material/TextField";
import Listbox from "../Listbox";
import {
  makeGETRequest,
  makeGETRequestOnUserService,
  makePOSTRequestOnUserService,
} from "../../Api/api";
import { IUser } from "../YieldCalculator/Interfaces";
import { BondDetails, UserDetails, ICashflow } from "./interfaces";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import toastr from "toastr";
import {
  StyledSelectInput,
  StyledTextInput,
} from "../StyledComponents/StyledInputs";
import FormHelperText from "@mui/material/FormHelperText";
import moment, { Moment } from "moment";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import {
  corporateBonds_moneyMarket_xirr,
  zcb_mld_xirr,
  gsec_sdl_xirr,
} from "./cashflowAPIs";
import { RouteComponentProps, withRouter } from "react-router-dom";
import CashflowPDF from "./CashflowPDF";
import generateCashflowPDFBlob from "./generateCashflowPDFBlob";

export interface IUserNameID {
  name: string;
  id: string;
}

export const sortDate = (a: any, b: any) => {
  const [dayA, monthA, yearA] = a.date.split("/").map(Number);
  const [dayB, monthB, yearB] = b.date.split("/").map(Number);
  const dateA = new Date(yearA, monthA - 1, dayA);
  const dateB = new Date(yearB, monthB - 1, dayB);
  return dateA.getTime() - dateB.getTime();
};

export function isGsecSdlTBill(securityType: string) {
  return [
    "State Development Loans",
    "Government Securities",
    "T Bill",
  ].includes(securityType);
}

export const callRelevantCashflowAPI = (
  bondDetails: BondDetails,
  offerPrice?: number,
  settlementDate?: Moment
) => {
  let securityType = bondDetails?.securityType.type;
  if (securityType === "Corporate Bonds")
    return corporateBonds_moneyMarket_xirr(
      bondDetails!,
      offerPrice ?? undefined,
      settlementDate ?? undefined
    );
  if (
    securityType === "Market Linked Debentures" ||
    securityType === "Zero Coupon Bonds"
  )
    return zcb_mld_xirr(
      bondDetails!,
      offerPrice ?? undefined,
      settlementDate ?? undefined
    );
  if (isGsecSdlTBill(securityType as string))
    return gsec_sdl_xirr(
      bondDetails!,
      offerPrice ?? undefined,
      settlementDate ?? undefined
    );
};

export const generateCashflow = async (
  bondDetails: BondDetails,
  offerPrice?: number,
  settlementDate?: Moment
) => {
  let faceValue = Number(bondDetails?.faceValue?.split(" ")[0]);
  let securityType = bondDetails?.securityType.type;
  try {
    const cashflowApisResponse = await callRelevantCashflowAPI(
      bondDetails,
      offerPrice ?? undefined,
      settlementDate ?? undefined
    );
    if (cashflowApisResponse && cashflowApisResponse.status === 200) {
      const { data } = cashflowApisResponse;
      const cashflowArray: ICashflow[] = [];
      if (securityType === "Corporate Bonds") {
        for (let i = 1; i < data.cashflow.AdjustedValueDate.length; i++) {
          const interest = Number(data.cashflow.Interest[i]);
          const maturity = Number(data.cashflow.Maturity[i]);
          if (interest !== 0) {
            const cashflow: ICashflow = {
              date: data.cashflow.AdjustedValueDate[i],
              amount: (interest * faceValue) / 100,
              type: "Interest",
            };
            cashflowArray.push(cashflow);
          }
          if (maturity !== 0) {
            const cashflow: ICashflow = {
              date: data.cashflow.AdjustedValueDate[i],
              amount: (maturity * faceValue) / 100,
              type: "Maturity",
            };
            cashflowArray.push(cashflow);
          }
        }
      }
      if (
        securityType === "Market Linked Debentures" ||
        securityType === "Zero Coupon Bonds"
      ) {
        for (let i = 1; i < data.cashflowDf.dates.length; i++) {
          const date = data.cashflowDf.dates[i];
          const amount = Number(data.cashflowDf.amounts[i]);
          const cashflow: ICashflow = {
            date: moment(date, "YYYY-MM-DD").format("DD/MM/YYYY").toString(),
            amount: (amount * faceValue) / 100,
            type: "Maturity",
          };
          cashflowArray.push(cashflow);
        }
      }
      if (isGsecSdlTBill(securityType as string)) {
        for (let i = 1; i < data.cashflow.ValueDate.length; i++) {
          const interest = Number(data.cashflow.Interest[i]);
          const maturity = Number(data.cashflow.Maturity[i]);
          if (interest !== 0) {
            const cashflow: ICashflow = {
              date: data.cashflow.ValueDate[i],
              amount: (interest * faceValue) / 100,
              type: "Interest",
            };
            cashflowArray.push(cashflow);
          }
          if (maturity !== 0) {
            const cashflow: ICashflow = {
              date: data.cashflow.ValueDate[i],
              amount: (maturity * faceValue) / 100,
              type: "Maturity",
            };
            cashflowArray.push(cashflow);
          }
        }
      }
      return cashflowArray;
    }
  } catch (error) {
    toastr.error("Error fetching cashflow.", JSON.stringify(error));
  }
};

export const initialInputs = {
  count: "",
  price: "",
  yield: "",
  principle: "",
  accruedInterest: "",
  stampDuty: "",
  totalConsideration: "",
  settlementDate: "",
  paymentProcess: "",
};

export const userTypeOptions = [
  { name: "Smest", value: "Smest" },
  { name: "Partner", value: "Partner" },
];

const CreateRFQOrder: React.FC<RouteComponentProps> = (props) => {
  const [userOpen, setUserOpen] = useState(false);
  const [users, setUsers] = useState<readonly IUserNameID[]>([]);
  const [user, setUser] = useState<IUserNameID | null>(null);
  const [isinOpen, setIsinOpen] = useState(false);
  const [isins, setIsins] = useState<readonly IUserNameID[]>([]);
  const [isin, setIsin] = useState<IUserNameID | null>(null);
  const [bondDetails, setBondDetails] = useState<BondDetails | null>(null);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [inputs, setInputs] = useState(initialInputs);
  const [cashflowRecords, setCashflowRecords] = React.useState<any[]>([]);
  const loadingUsers = userOpen && users.length === 0;
  const loadingIsins = isinOpen && isins.length === 0;
  const [userType, setUserType] = useState<{ name: string; value: string; } | null>(userTypeOptions[0]);
  const [partnerOpen, setPartnerOpen] = useState(false);
  const [partners, setPartners] = useState<readonly IUserNameID[]>([]);
  const [partner, setPartner] = useState<IUserNameID | null>(null);
  const [partnerMode, setPartnerMode] = useState<"self" | "client" | null>(null);
  const [client, setClient] = useState<IUserNameID | null>(null);
  const [clients, setClients] = useState<IUserNameID[]>([]);
  const [partnerData, setPartnerData] = useState<any[]>([]);

  const loadingPartners = partnerOpen && partners.length === 0;

  let faceValue = Number(bondDetails?.faceValue?.split(" ")[0]);
  let securityType = bondDetails?.securityType.type;

  useEffect(() => {
    let active = true;
    if (!loadingUsers) {
      return undefined;
    }
    makeGETRequestOnUserService("/user/user_list/")
      .then((response) => {
        if (response && response.status === 200) {
          const { data } = response;
          if (active) {
            const optionsArray = (data as IUser[])
              .filter((item) => item.areKYCDetailsVerified)
              .map((item) => ({
                name: `${item.firstName} ${item.lastName} - ${item.mobileNumber}`,
                id: item.uuid,
              }));
            setUsers([...optionsArray]);
          }
        }
      })
      .catch((error) => toastr.error(error));
    return () => {
      active = false;
    };
  }, [loadingUsers]);

  useEffect(() => {
    if (!userOpen) {
      setUsers([]);
    }
  }, [userOpen]);

  useEffect(() => {
    let active = true;
    if (!loadingPartners) return undefined;
    makeGETRequestOnUserService("/partneruser/user_list/")
      .then((response) => {
        if (response?.status === 200 && active) {
          const { data } = response;
          setPartnerData(data);
          const partnerOptions = data.map((item: any) => ({
            name: `${item.first_name} ${item.last_name} - ${item.mobile_number}`,
            id: item.uuid,
          }));
          setPartners(partnerOptions);
        }
      })
      .catch((error) => toastr.error(error));
    return () => {
      active = false;
    };
  }, [loadingPartners]);

  useEffect(() => {
    if (!partnerOpen) {
      setPartners([]);
    }
  }, [partnerOpen]);

  const handlePartnerChange = (_: any, newValue: IUserNameID | null) => {
    setPartner(newValue);
    setClient(null);
    if (!newValue) {
      setClients([]);
      return;
    }
    const selectedPartner = partnerData.find(
      (item: any) => item.uuid === newValue.id
    );
    const clientOptions =
      selectedPartner?.clients?.map((client: any) => ({
        name: `${client.first_name} ${client.last_name}`,
        id: client.uuid,
      })) || [];
    setClients(clientOptions);
  };

  useEffect(() => {
    let active = true;
    if (!loadingIsins) {
      return undefined;
    }
    makeGETRequest(`/admin/security_master/list/formatted/`)
      .then((response) => {
        if (response && response.status === 200) {
          const { data } = response;
          if (active) {
            const optionsArray = data.map((item: any) => ({
              name: `${item.isin} - ${item.securityName}`,
              id: item.isin,
            }));
            setIsins([...optionsArray]);
          }
        }
      })
      .catch((error) => toastr.error(error));
    return () => {
      active = false;
    };
  }, [loadingIsins]);

  useEffect(() => {
    if (!isinOpen) {
      setIsins([]);
    }
  }, [isinOpen]);

  useEffect(() => {
    if (isin) {
      makeGETRequest(`admin/security_master/get/${isin.id}/`)
        .then(async (response) => {
          if (response && response.status === 200) {
            const { data } = response;
            const cashflow = await generateCashflow(data);
            cashflow?.length && setCashflowRecords(cashflow);
            setBondDetails(data);
          }
        })
        .catch((error) => toastr.error(error));
    } else {
      setBondDetails(null);
      setInputs(initialInputs);
    }
  }, [isin]);

  useEffect(() => {
    if (user) {
      makeGETRequestOnUserService(`user/get/${user.id}/`)
        .then((response) => {
          if (response && response.status === 200) {
            const { data } = response;
            setUserDetails(data);
          }
        })
        .catch((error) => toastr.error(error));
    } else {
      setUserDetails(null);
    }
  }, [user]);

  useEffect(() => {
    if (partnerMode === "self" && partner) {
      makeGETRequestOnUserService(
        `/partneruser/user/get/${partner.id}/?user_type=partner`
      )
        .then((response) => {
          if (response?.status === 200) {
            setUserDetails(response.data);
          }
        })
        .catch((error) => toastr.error(error));
    }

    if (partnerMode === "client" && client) {
      makeGETRequestOnUserService(
        `/partneruser/user/get/${client.id}/?user_type=client`
      )
        .then((response) => {
          if (response?.status === 200) {
            setUserDetails(response.data);
          }
        })
        .catch((error) => toastr.error(error));
    }
  }, [partnerMode, partner, client]);

  const inputData = useMemo(
    () => [
      {
        label: "Sell Yield",
        value: inputs.yield,
        key: "yield",
      },
      {
        label: "Sell Price",
        value: inputs.price,
        key: "price",
      },
      {
        label: "No of Bonds",
        value: inputs.count,
        key: "count",
      },
      {
        label: "Principal Amount",
        value: inputs.principle,
        key: "principle",
      },
      {
        label: "Modified Accrued Interest",
        value: inputs.accruedInterest,
        key: "accruedInterest",
      },
      {
        label: "Stamp Duty",
        value: inputs.stampDuty,
        key: "stampDuty",
      },
      {
        label: "Total Consideration without Stamp Duty",
        value: inputs.totalConsideration,
        key: "totalConsideration",
        helperText: `Total Consideration with Stamp Duty - ₹${Number(inputs.totalConsideration) + Number(inputs.stampDuty)
          }`,
      },
      {
        label: "Settlement Date",
        value: inputs.settlementDate,
        key: "settlementDate",
      },
      {
        label: "Payment Process",
        value: inputs.paymentProcess,
        key: "paymentProcess",
      },
    ],
    [
      inputs.count,
      inputs.accruedInterest,
      inputs.totalConsideration,
      inputs.settlementDate,
      inputs.paymentProcess,
      inputs.stampDuty,
      inputs.principle,
      inputs.yield,
      inputs.price,
    ]
  );

  const orderData = useMemo(
    () => [
      { label: "User", value: userDetails?.uuid ?? "", helperText: "" },
      { label: "ISIN", value: bondDetails?.isin ?? "", helperText: "" },
      {
        label: "Bond Type",
        value: isGsecSdlTBill(securityType as string) ? "GSEC" : "ICDM",
        helperText: "",
      },
      {
        label: "Minimum Order Value",
        value: isNaN(faceValue * Number(inputs.count))
          ? ""
          : faceValue * Number(inputs.count),
        helperText: "",
      },
      {
        label: "Value",
        value: isNaN(faceValue * Number(inputs.count))
          ? ""
          : faceValue * Number(inputs.count),
        helperText: "",
      },
      {
        label: "Sell Price",
        value: bondDetails?.quoteDetails?.sellPrice ?? "",
        helperText: "",
      },
      {
        label: "Security Name",
        value: bondDetails?.securityName ?? "",
        helperText: "",
      },
      {
        label: "Settlement Type",
        value:
          inputs.paymentProcess === "PAYMENT GATEWAY"
            ? 0
            : isGsecSdlTBill(securityType as string)
              ? 1
              : 0,
        helperText: "",
      },
      { label: "Deal Type", value: "OBP", helperText: "" },
      { label: "Bid Offer", value: "BID", helperText: "" },
      {
        label: "Rating",
        value:
          bondDetails?.ratingOrganizationMasters[0]?.rating?.rating ?? "AAA",
        helperText: "",
      },
      {
        label: "Rating Agency",
        value:
          bondDetails?.ratingOrganizationMasters[0]?.organization?.name ??
          "CRISIL",
        helperText: "",
      },
      {
        label: "Type of Yield",
        value: bondDetails?.callOptions?.length ? "YTC" : "YTM",
        helperText: "",
      },
      { label: "Deal Time Hours", value: moment().format("H"), helperText: "" },
      {
        label: "Deal Time Minutes",
        value: moment().format("mm"),
        helperText: "",
      },
      { label: "OTO Participant Name", value: "SMEST", helperText: "" },
      {
        label: "Buyer Client Name",
        value:
          (userDetails?.ucc_external_id
            ? userDetails?.ucc_external_id
            : userDetails?.smestID) ?? "",
        helperText: "",
      },
      {
        label: "Buyer Full Name",
        value: userDetails?.panCard.name ?? "",
        helperText: "",
      },
      { label: "Seller Name", value: "SMEST", helperText: "" },
      {
        label: "Initiator IFSC",
        value:
          userDetails?.bankDetails?.find((item) => item.is_default)?.ifscCode ??
          "",
        helperText: "",
      },
      {
        label: "Initiator Bank Account Number",
        value:
          userDetails?.bankDetails?.find((item) => item.is_default)
            ?.accountNumber ?? "",
        helperText: "",
      },
      {
        label: "Initiator DP Type",
        value:
          userDetails?.dematDetails?.find((item) => item.is_default)?.dpType ??
          "",
        helperText: "",
      },
      {
        label: "Initiator DP ID",
        value:
          userDetails?.dematDetails
            ?.find((item) => item.is_default)
            ?.id.substring(0, 8) ?? "",
        helperText: "",
      },
      {
        label: "Initiator Client ID",
        value:
          (userDetails?.dematDetails?.find((item) => item.is_default)
            ?.dpType === "NSDL"
            ? userDetails?.dematDetails
              ?.find((item) => item.is_default)
              ?.id.substring(8)
            : userDetails?.dematDetails?.find((item) => item.is_default)?.id) ??
          "",
        helperText: "",
      },
      { label: "Initiator Name", value: "SMEST", helperText: "" },
      { label: "Responder IFSC", value: "KKBK0000661", helperText: "" },
      {
        label: "Responder Bank Account Number",
        value: "9813006261",
        helperText: "",
      },
      { label: "Responder DP Type", value: "NSDL", helperText: "" },
      { label: "Responder DP ID", value: "IN303655", helperText: "" },
      { label: "Responder Client ID", value: "10255881", helperText: "" },
      { label: "Pro Client", value: "CLIENT", helperText: "" },
    ],
    [
      bondDetails,
      userDetails,
      inputs.count,
      inputs.paymentProcess,
      securityType,
    ]
  );

  const handleOrder = () => {
    let url = "order_management_v2/orders/";
    let uploadUrl = "order_management_v2/cashflow_pdf/upload/";
    let userPayload = {};
    if (userType?.value === "Smest") {
      userPayload = {
        user: userDetails?.uuid,
      };
    } else if (
      userType?.value === "Partner" &&
      partnerMode === "self"
    ) {
      url = "order_management_v2/orders/?type=partner";
      uploadUrl = "order_management_v2/cashflow_pdf/upload/?type=partner";
      userPayload = {
        partner_user: partner?.id,
      };
    } else if (
      userType?.value === "Partner" &&
      partnerMode === "client"
    ) {
      url = "order_management_v2/orders/?type=client";
      uploadUrl = "order_management_v2/cashflow_pdf/upload/?type=client";
      userPayload = {
        client_user: client?.id,
      };
    }

    let payload = {
      ...userPayload,
      bond_type: isGsecSdlTBill(securityType as string) ? "GSEC" : "ICDM",
      isin: bondDetails?.isin,
      rating:
        bondDetails?.ratingOrganizationMasters[0]?.rating?.rating ?? "AAA",
      rating_agency:
        bondDetails?.ratingOrganizationMasters[0]?.organization?.name ??
        "CRISIL",
      value: faceValue * Number(inputs.count),
      minimum_order_value: faceValue * Number(inputs.count),
      type_of_yield: bondDetails?.callOptions?.length ? "YTC" : "YTM",
      sell_yield: inputs.yield,
      sell_price: inputs.price,
      dirtyprice:
        (Number(inputs.totalConsideration) /
          (faceValue * Number(inputs.count))) *
        100,
      sellerclientcode:
        process.env.REACT_APP_ENVIRONMENT === "prod" ? "SMEST" : "SCPL",
      quotetype: "BID",
      settlement_type:
        inputs.paymentProcess === "PAYMENT GATEWAY"
          ? 0
          : isGsecSdlTBill(securityType as string)
            ? 1
            : 0,
      deal_time_hours: moment().format("H"),
      deal_time_minutes: moment().format("mm"),
      buyer_client_name: userDetails?.ucc_external_id
        ? userDetails?.ucc_external_id
        : userDetails?.smestID,
      no_of_bonds: Number(inputs.count),
      principal_amount: Number(inputs.principle),
      modified_accrued_interest: Number(inputs.accruedInterest),
      stamp_duty: Number(inputs.stampDuty),
      total_consideration: Number(inputs.totalConsideration),
      buyer_full_name: userDetails?.panCard.name,
      initiator_ifsc: userDetails?.bankDetails.find((item) => item.is_default)
        ?.ifscCode,
      initiator_bank_account_number: userDetails?.bankDetails.find(
        (item) => item.is_default
      )?.accountNumber,
      initiator_dp_type: userDetails?.dematDetails.find(
        (item) => item.is_default
      )?.dpType,
      initiator_dpid: userDetails?.dematDetails
        .find((item) => item.is_default)
        ?.id.substring(0, 8),
      initiator_client_id:
        userDetails?.dematDetails.find((item) => item.is_default)?.dpType ===
          "NSDL"
          ? userDetails?.dematDetails
            .find((item) => item.is_default)
            ?.id.substring(8)
          : userDetails?.dematDetails.find((item) => item.is_default)?.id,
      settlement_date: moment(inputs.settlementDate, "YYYY-MM-DD").format(
        "MMM DD,YYYY"
      ),
      security_name: bondDetails?.securityName,
      payment_process: inputs.paymentProcess,
      cashflow_records: cashflowRecords?.map((item) => ({
        ...item,
        amount: (Number(item.amount) * Number(inputs.count)).toFixed(4),
      })),
      s3_url: bondDetails?.issuerOfSecurity.s3Url,
      last_call_date: bondDetails?.callOptions
        ?.sort((a, b) => sortDate(a, b))
        .at(-1)?.date,
      last_maturity_date: bondDetails?.maturities
        ?.sort((a, b) => sortDate(a, b))
        .at(-1)?.date,
    };
    makePOSTRequestOnUserService(url, payload)
      .then(async ({ status, data }) => {
        if (status === 201) {
          const pdfBlob = await generateCashflowPDFBlob();
          let formData = new FormData();
          formData.append("cashflow_pdf", pdfBlob, "cashflow.pdf");
          formData.append("order_id", data.uuid);
          await makePOSTRequestOnUserService(
            uploadUrl,
            formData
          );
          toastr.success("Order placed successfully.");
          props.history.push("/rfq_orders");
        }
      })
      .catch((error) => toastr.error(error));
  };

  return (
    <>
      <Box
        sx={{
          maxWidth: ["90%", "80%"],
          mx: "auto",
          my: 5,
          bgcolor: "#FFFFFF",
          p: 2,
          borderRadius: 5,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box sx={{ width: ["100%", 400], mx: "auto" }}>
          <Autocomplete
            id="user-type-select"
            value={userType}
            onChange={(_, newValue) => {
              setUserType(newValue);
            }}
            options={userTypeOptions}
            getOptionLabel={(option) => option?.name || ""}
            size="medium"
            sx={sxRoot}
            popupIcon={<ExpandMoreIcon />}
            renderInput={(params) => (
              <TextField
                {...params}
                label="User Type"
                sx={(theme) => sxText(theme)}
                fullWidth
                margin="dense"
                variant="standard"
                InputLabelProps={{ shrink: true }}
              />
            )}
            renderOption={(props, option, { selected }) => (
              <Typography {...props} sx={sxTypo(selected)} variant="h6">
                {option.name}
              </Typography>
            )}
            PaperComponent={({ children }) => (
              <Box sx={sxBox}>
                {children}
              </Box>
            )}
            ListboxComponent={Listbox}
          />

          {userType?.value === "Smest" && (
            <Autocomplete
              id={"user-select"}
              value={user}
              onChange={(_, newValue) => {
                setUser(newValue);
              }}
              open={userOpen}
              onOpen={() => {
                setUserOpen(true);
              }}
              onClose={() => {
                setUserOpen(false);
              }}
              isOptionEqualToValue={(option, value) => option === value}
              getOptionLabel={(option) => option.name}
              options={users}
              loading={loadingUsers}
              size="medium"
              sx={sxRoot}
              popupIcon={<ExpandMoreIcon />}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select User"
                  sx={(theme) => sxText(theme)}
                  id={"user-select"}
                  fullWidth
                  margin="dense"
                  variant="standard"
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingUsers ? (
                          <CircularProgress color="error" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option, { selected }) => (
                <Typography {...props} sx={sxTypo(selected)} variant="h6">
                  {option.name}
                </Typography>
              )}
              PaperComponent={({ children }) => {
                return <Box sx={sxBox}>{children}</Box>;
              }}
              ListboxComponent={Listbox}
            />
          )}

          {userType?.value === "Partner" && (
            <>
              <Autocomplete
                id="partner-select"
                value={partner}
                onChange={handlePartnerChange}
                open={partnerOpen}
                onOpen={() => {
                  setPartnerOpen(true);
                }}
                onClose={() => {
                  setPartnerOpen(false);
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) => option?.name ?? ""}
                options={partners}
                loading={loadingPartners}
                size="medium"
                sx={sxRoot}
                popupIcon={<ExpandMoreIcon />}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Partner"
                    sx={(theme) => sxText(theme)}
                    fullWidth
                    margin="dense"
                    variant="standard"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingPartners && (
                            <CircularProgress color="error" size={20} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option, { selected }) => (
                  <Typography {...props} sx={sxTypo(selected)} variant="h6">
                    {option.name}
                  </Typography>
                )}
                PaperComponent={({ children }) => (
                  <Box sx={sxBox}>{children}</Box>
                )}
                ListboxComponent={Listbox}
              />

              {partner && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    mt: 2,
                    mb: 2,
                    width: "100%",
                  }}
                >
                  <Button
                    fullWidth
                    variant={partnerMode === "self" ? "contained" : "outlined"}
                    onClick={() => setPartnerMode("self")}
                    sx={{
                      flex: 1,
                      bgcolor: partnerMode === "self" ? "#262349" : "transparent",
                      color: partnerMode === "self" ? "#fff" : "#262349",
                      borderColor: "#262349",
                    }}
                  >
                    Self
                  </Button>

                  <Button
                    fullWidth
                    variant={partnerMode === "client" ? "contained" : "outlined"}
                    onClick={() => setPartnerMode("client")}
                    sx={{
                      flex: 1,
                      bgcolor: partnerMode === "client" ? "#262349" : "transparent",
                      color: partnerMode === "client" ? "#fff" : "#262349",
                      borderColor: "#262349",
                    }}
                  >
                    Client
                  </Button>
                </Box>
              )}
            </>
          )}

          {userType?.value === "Partner" &&
            partnerMode === "client" && (
              <Autocomplete
                id="client-select"
                value={client}
                onChange={(_, newValue) => setClient(newValue)}
                options={clients}
                getOptionLabel={(option) => option?.name ?? ""}
                sx={sxRoot}
                popupIcon={<ExpandMoreIcon />}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Client"
                    sx={(theme) => sxText(theme)}
                    fullWidth
                    margin="dense"
                    variant="standard"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            )}

          <Autocomplete
            id={"isin-select"}
            value={isin}
            onChange={(_, newValue) => {
              setIsin(newValue);
            }}
            open={isinOpen}
            onOpen={() => {
              setIsinOpen(true);
            }}
            onClose={() => {
              setIsinOpen(false);
            }}
            isOptionEqualToValue={(option, value) => option === value}
            getOptionLabel={(option) => option.name}
            options={isins}
            loading={loadingIsins}
            size="medium"
            sx={sxRoot}
            popupIcon={<ExpandMoreIcon />}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Isin"
                sx={(theme) => sxText(theme)}
                id={"isin-select"}
                fullWidth
                margin="dense"
                variant="standard"
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingUsers ? (
                        <CircularProgress color="error" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option, { selected }) => (
              <Typography {...props} sx={sxTypo(selected)} variant="h6">
                {option.name}
              </Typography>
            )}
            PaperComponent={({ children }) => {
              return <Box sx={sxBox}>{children}</Box>;
            }}
            ListboxComponent={Listbox}
          />
        </Box>
        <Grid container spacing={2} sx={{ mt: 4 }}>
          {inputData.map((item, index) => (
            <Grid key={index} item xs={12} sm={4} md={3}>
              {item.key === "paymentProcess" ? (
                <FormControl fullWidth variant="standard" size="small">
                  <InputLabel
                    shrink
                    htmlFor={`${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {item.label}
                  </InputLabel>
                  <Select
                    id={`${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    value={item.value}
                    label={item.label}
                    onChange={(event) =>
                      setInputs((prev) => ({
                        ...prev,
                        paymentProcess: event.target.value,
                      }))
                    }
                    input={<StyledSelectInput />}
                  >
                    <MenuItem value={"BANK TRANSFER"}>BANK TRANSFER</MenuItem>
                    <MenuItem value={"PAYMENT GATEWAY"}>
                      PAYMENT GATEWAY
                    </MenuItem>
                  </Select>
                </FormControl>
              ) : item.key === "settlementDate" ? (
                <FormControl fullWidth variant="standard" size="small">
                  <InputLabel
                    shrink
                    htmlFor={`${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {item.label}
                  </InputLabel>
                  <StyledTextInput
                    id={`${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    value={item.value}
                    type="date"
                    onChange={(event) =>
                      setInputs((prev) => ({
                        ...prev,
                        settlementDate: event.target.value,
                      }))
                    }
                  />
                </FormControl>
              ) : (
                <FormControl fullWidth variant="standard">
                  <InputLabel
                    shrink
                    htmlFor={`${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {item.label}
                  </InputLabel>
                  <StyledTextInput
                    id={`${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    value={item.value}
                    onChange={(event) =>
                      setInputs((prev) => ({
                        ...prev,
                        [item.key]: event.target.value,
                      }))
                    }
                    type="number"
                    inputMode="numeric"
                  />
                  {item.helperText && (
                    <FormHelperText
                      sx={{
                        color: "#45E142",
                        fontSize: 10,
                        ml: 1,
                      }}
                    >
                      {item.helperText}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            </Grid>
          ))}
        </Grid>
        <Box
          component="hr"
          sx={{
            borderColor: "#7659EE",
            opacity: 0.5,
            my: 4,
            width: "100%",
          }}
        />
        <Grid container spacing={2}>
          {orderData.map((item, index) => (
            <Grid key={index} item xs={12} sm={4} md={3}>
              <FormControl fullWidth variant="standard">
                <InputLabel
                  shrink
                  htmlFor={`${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {item.label}
                </InputLabel>
                <StyledTextInput
                  id={`${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  disabled
                  value={item.value}
                />
                {item.helperText && (
                  <FormHelperText
                    sx={{
                      color: "#45E142",
                      fontSize: 10,
                      ml: 1,
                    }}
                  >
                    {item.helperText}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
          ))}
        </Grid>
        <Button
          sx={{ mt: 4 }}
          variant="contained"
          onClick={handleOrder}
          disabled={
            !isin ||
            (
              userType?.value === "Smest" &&
              !user
            ) ||
            (
              userType?.value === "Partner" &&
              partnerMode === "self" &&
              !partner
            ) ||
            (
              userType?.value === "Partner" &&
              partnerMode === "client" &&
              !client
            )
          }
        >
          Order
        </Button>
      </Box>
      <CashflowPDF
        bondDetails={bondDetails}
        inputvalues={inputs}
        cashflow={cashflowRecords}
      />
    </>
  );
};

export default withRouter(CreateRFQOrder);
